/**
 * When a pane shows someone's held last frame while their call moves between
 * the direct connection and the bridge (heldFrames.web.ts keeps the frame).
 * Pure, so the rule is tested without a browser.
 */
/**
 * How long a held frame may stand in for someone's video. A move normally
 * brings the first new frame within a second; past this the pane shows what
 * it would have shown without one, rather than a picture that looks frozen.
 */
export const HELD_FRAME_MAX_MS = 4000;
/**
 * How long after the new video's first frame the held one is let go: past the
 * pane's fade-in (PEER_PANE_FADE_MS, 450 ms, in components/web/PeerWaitingPane.web.tsx),
 * which it sits under.
 */
export const HELD_FRAME_RELEASE_AFTER_PLAY_MS = 600;
/**
 * Whether the pane shows the held frame: there is one and it is still fresh.
 * It lies over the pane's video element while that element waits for the new
 * track's first frame, and fades out as the video fades in (so neither the
 * pane's dark ground nor a half-faded picture shows in between); the held
 * frame is let go HELD_FRAME_RELEASE_AFTER_PLAY_MS after the new video played.
 *
 * @param {Object} options - The pane's facts.
 * @param {number|undefined} options.heldAt - When the frame was held, if one is.
 * @param {number} options.now - The time now.
 * @returns {boolean}
 */
export function shouldShowHeldFrame({ heldAt, now }) {
    return typeof heldAt === 'number' && now - heldAt >= 0 && now - heldAt <= HELD_FRAME_MAX_MS;
}
/**
 * The tracks known to have played, without the ones no longer on any pane.
 *
 * A call moving back to the bridge re-adds the very track objects it had
 * before it went direct, with no frame flowing yet; a track remembered as
 * played would show its empty element at once instead of waiting for a frame.
 * The same set comes back when nothing left, so React keeps its state.
 *
 * @param {Set} ready - Tracks that have played.
 * @param {Iterable} present - The tracks on the panes now.
 * @returns {Set}
 */
export function pruneReadyTracks(ready, present) {
    const onPanes = new Set(present);
    const kept = Array.from(ready).filter(track => onPanes.has(track));
    return kept.length === ready.size ? ready : new Set(kept);
}
